import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, isAuthError } from '../../api/client'
import { OwnerPreviewConsole, type ProjectSummary, type MemberSummary } from './olivium/preview'

/**
 * V2 Owner Preview — route wrapper.
 *
 * Registered standalone + auth-gated (see router.tsx):
 *   /square/command-center/olivium/portal-factory/preview
 *
 * AccessGate alone is not owner-only (some allowlist configurations approve
 * non-admin active members), so this route ALSO requires isAdmin
 * (useAuth().isAdmin, level >= 80) before rendering anything — a non-admin
 * authenticated operator sees an honest "owner/admin only" denial, never the
 * console. Every data read here (projects, project members) is GET-only and
 * reuses an existing owner-scoped, withAuth()-gated endpoint — this route
 * introduces no new API surface.
 */
function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

export default function SquareCodeOwnerPreviewRoute() {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()

  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [projectsError, setProjectsError] = useState<string | null>(null)
  const [projectsAuthRequired, setProjectsAuthRequired] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)

  const [members, setMembers] = useState<MemberSummary[]>([])
  const [membersActivated, setMembersActivated] = useState(false)
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    setProjectsLoading(true)
    api.projects.list()
      .then((res) => {
        if (cancelled) return
        setProjects((res.projects || []).map((p) => ({ id: p.id, name: p.name, client_name: p.client_name })))
        setProjectsLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (isAuthError(err)) setProjectsAuthRequired(true)
        else setProjectsError(formatError(err))
        setProjectsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  const loadMembers = useCallback((projectId: string) => {
    setMembersLoading(true)
    setMembersError(null)
    api.projects.access(projectId)
      .then((res) => {
        setMembersActivated(res.activated)
        setMembers((res.members || []).map((m) => ({ id: m.id, role: m.role, status: m.status })))
        setMembersLoading(false)
      })
      .catch((err) => {
        setMembersError(formatError(err))
        setMembersLoading(false)
      })
  }, [])

  const onSelectProject = useCallback((id: string | null) => {
    setSelectedProjectId(id)
    setMembers([])
    setMembersActivated(false)
    setMembersError(null)
    if (id) loadMembers(id)
  }, [loadMembers])

  return (
    <OwnerPreviewConsole
      isAdmin={isAdmin}
      projects={projects}
      projectsLoading={projectsLoading}
      projectsError={projectsError}
      projectsAuthRequired={projectsAuthRequired}
      selectedProjectId={selectedProjectId}
      onSelectProject={onSelectProject}
      membersActivated={membersActivated}
      members={members}
      membersLoading={membersLoading}
      membersError={membersError}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
    />
  )
}
